import React from "react";
import {
  formatDateTime,
  getPlaceName,
  parseCancellationReason,
  pickSafeCompanyFields,
} from "../utils";

const BookingSummaryCard = ({ booking, company }) => {
  if (!booking) return null;

  const statusSlug = String(booking.bookingStatus || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const pickupName = getPlaceName(booking.pickup);
  const dropName = getPlaceName(booking.destination);
  const currency = booking.currency ? String(booking.currency) : "";
  const fare =
    booking.fare != null ? `${booking.fare}${currency ? ` ${currency}` : ""}` : "—";
  const finalPrice =
    booking.finalPrice != null
      ? `${booking.finalPrice}${currency ? ` ${currency}` : ""}`
      : "—";

  const cancellation = parseCancellationReason(booking.cancellationReason);
  const companyRows = pickSafeCompanyFields(company);

  return (
    <section className="logs-summary-card">
      <div className="logs-summary-header">
        <div>
          <h3>Booking overview</h3>
          <p className="logs-summary-sub">
            Fare, passenger, route, and organisation details
          </p>
        </div>
        {booking.bookingStatus ? (
          <span
            className={`logs-status-pill${
              statusSlug ? ` logs-status-pill--${statusSlug}` : ""
            }`}
          >
            {booking.bookingStatus}
          </span>
        ) : null}
      </div>

      {booking.cancelled && cancellation?.reasonText ? (
        <div className="logs-cancellation-banner">
          <strong>Cancellation</strong>
          <p>{cancellation.reasonText}</p>
          {booking.cancellationTime ? (
            <small>{formatDateTime(booking.cancellationTime)}</small>
          ) : null}
          {cancellation.location &&
          (cancellation.location.latitude != null ||
            cancellation.location.longitude != null) ? (
            <small className="logs-cancellation-loc">
              Location: {cancellation.location.latitude ?? "—"},{" "}
              {cancellation.location.longitude ?? "—"}
            </small>
          ) : null}
          {cancellation.pictures?.length ? (
            <div className="logs-cancellation-pics">
              {cancellation.pictures.map((url) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="logs-cancellation-pic-link"
                >
                  View photo
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="logs-summary-grid">
        <div className="logs-summary-field">
          <span>Guest</span>
          <strong>{booking.guestName || "—"}</strong>
        </div>
        <div className="logs-summary-field">
          <span>Fare</span>
          <strong>{fare}</strong>
        </div>
        <div className="logs-summary-field">
          <span>Final price</span>
          <strong>{finalPrice}</strong>
        </div>
        <div className="logs-summary-field">
          <span>Pickup</span>
          <strong title={pickupName}>{pickupName}</strong>
        </div>
        <div className="logs-summary-field logs-summary-field--wide">
          <span>Drop-off</span>
          <strong title={dropName}>{dropName}</strong>
        </div>
        {booking.refId ? (
          <div className="logs-summary-field">
            <span>Reference</span>
            <strong>{booking.refId}</strong>
          </div>
        ) : null}
        {booking.serviceType ? (
          <div className="logs-summary-field">
            <span>Service</span>
            <strong>{booking.serviceType}</strong>
          </div>
        ) : null}
        <div className="logs-summary-field">
          <span>Payout ID</span>
          <strong title="invoiceNo">{booking.invoiceNo || "—"}</strong>
        </div>
        <div className="logs-summary-field">
          <span>Payin ID</span>
          <strong title="receiptNo">{booking.receiptNo || "—"}</strong>
        </div>
      </div>

      {companyRows.length ? (
        <div className="logs-company-block">
          <h4>Company</h4>
          <dl className="logs-company-dl">
            {companyRows.map((row) => (
              <div key={row.label} className="logs-company-row">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </section>
  );
};

export default BookingSummaryCard;
